import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, THRIFT_CAT, KIDS_TAG, KIDS_AGES, ageLabel, withAgeTag } from "@/lib/lamka";
import { Switch } from "@/components/ui/switch";
import { uploadImage } from "@/lib/order";
import { withAdminUnlock } from "@/lib/admin";
import { webApi } from "@/lib/webApi";
import { useAuth } from "@/context/AuthContext";
import { ThriftPhotos } from "./ThriftPhotos";
import { CutoutPreview } from "./CutoutPreview";

const CONDITIONS = ["Like New", "Good", "Fair"];
const UPI_KEY = "lamka_admin_upi";
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];
const DELIVERY = ["Same Day", "5-7 Days"];
const EMPTY = { title: "", price: "", size: "", colour: "", condition: "", delivery_time: DELIVERY[0], kids: false, age: "" };
const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1";
const input = "w-full bg-white border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-3 py-2.5 text-base sm:text-sm";

const Field = ({ name, text, form, setForm, type = "text", required, ...rest }) => (
  <label className="block">
    <span className={label}>{text}{required && <span className="text-[#B38F46]"> *</span>}</span>
    <input data-testid={`thrift-upload-${name}`} type={type} value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className={input} {...rest} />
  </label>
);

const slug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");
const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join(" · ");
  return (typeof d === "string" && d) || "Couldn't post the piece. Please try again.";
};

const Choices = ({ name, text, options, display = (o) => o, form, setForm, required = true, cols = "grid-cols-3" }) => (
  <div>
    <span className={label}>{text}{required && <span className="text-[#B38F46]"> *</span>}</span>
    <div className={`grid ${cols} gap-2`} role="radiogroup">
      {options.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={form[name] === c} data-testid={`thrift-upload-${name.replace("_", "-")}-${slug(display(c))}`} onClick={() => setForm({ ...form, [name]: !required && form[name] === c ? "" : c })} className={`min-h-11 px-1 border text-sm leading-tight transition-colors ${form[name] === c ? "border-[#C5A059] bg-[#C5A059]/15 text-[#0F0F0F]" : "border-[#0F0F0F]/15 bg-white text-[#71717A] hover:border-[#C5A059]/60"}`}>{display(c)}</button>
      ))}
    </div>
  </div>
);

export const ThriftUploadDialog = ({ open, onOpenChange, onCreated, askPassword }) => {
  const [form, setForm] = useState({ ...EMPTY, upi: localStorage.getItem(UPI_KEY) || "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const { user } = useAuth();

  const missing = !files.length ? "Add at least one photo." : !form.title.trim() ? "Add a title." : !(Number(form.price) > 0) ? "Add a price above ₹0." : !form.condition ? "Pick the condition." : "";

  const submit = async (e) => {
    e.preventDefault();
    if (missing) return toast.error(missing);
    try {
      const images = [];
      for (let i = 0; i < files.length; i++) {
        setBusy(`Uploading photo ${i + 1} of ${files.length}…`);
        images.push(await uploadImage(files[i]));
      }
      setBusy("Posting to the rack…");
      const upi = form.upi.trim();
      const body = { title: form.title.trim(), price: Number(form.price), category: THRIFT_CAT, images, stock: 1, condition: form.condition, delivery_time: form.delivery_time, ...(form.size && { size: form.size }), ...(form.colour.trim() && { colour: form.colour.trim() }), ...(upi && { seller_upi: upi }), ...(form.kids && { description: withAgeTag(KIDS_TAG, form.age) }) };
      const { data } = await withAdminUnlock(() => api.post("/listings", body), askPassword);
      if (upi) localStorage.setItem(UPI_KEY, upi);
      onCreated({ ...body, seller_name: user?.name, ...data });
      if (data?.id) {
        setBusy("Removing background…");
        const cutForm = new FormData();
        if (preview?.file === files[0]) cutForm.append("file", preview.blob, "cutout.png");
        const cut = await webApi.post(`/thrift/cutout/${data.id}`, cutForm).then((r) => r.data).catch(() => ({ status: "failed" }));
        if (cut.status !== "done") toast("Couldn't cut out the background — the original photo will hang on the rail.");
      }
      toast.success("Thrift piece is on the rack", { action: { label: "View", onClick: () => window.location.assign("/#thrift") } });
      setForm({ ...EMPTY, upi });
      setFiles([]);
      onOpenChange(false);
    } catch (err) {
      if (err.message !== "cancelled") toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="thrift-upload-dialog" className="rounded-none max-w-lg bg-[#FBF9F5] max-h-[92vh] overflow-y-auto">
        <DialogTitle className="font-display text-2xl">Upload Thrift Item</DialogTitle>
        <DialogDescription className="text-sm text-[#71717A]">Goes straight onto the home page Thrift Closet. Each piece is one of a kind.</DialogDescription>
        <form onSubmit={submit} className="space-y-4 mt-2" noValidate>
          <div>
            <span className={label}>Photos (up to 5)<span className="text-[#B38F46]"> *</span></span>
            <ThriftPhotos files={files} setFiles={setFiles} disabled={!!busy} />
            {files[0] && <div className="mt-2"><CutoutPreview file={files[0]} disabled={!!busy} onReady={setPreview} onRetake={(f) => setFiles((cur) => [f, ...cur.slice(1)])} /></div>}
          </div>
          <Field name="title" text="Title" required form={form} setForm={setForm} maxLength={120} />
          <Field name="price" text="Price (₹)" type="number" inputMode="decimal" min="1" required form={form} setForm={setForm} />
          <Choices name="size" text="Size (optional — tap again to clear)" options={SIZES} required={false} cols="grid-cols-4" form={form} setForm={setForm} />
          <Field name="colour" text="Colour" form={form} setForm={setForm} />
          <Choices name="condition" text="Condition" options={CONDITIONS} form={form} setForm={setForm} />
          <Choices name="delivery_time" text="Delivery time" options={DELIVERY} cols="grid-cols-2" form={form} setForm={setForm} />
          <label className="flex items-center justify-between gap-3 border border-[#0F0F0F]/10 bg-white px-3 py-3">
            <span className="text-sm">For kids <span className="text-xs text-[#71717A]">— shows on the Kids Thrift rail</span></span>
            <Switch data-testid="thrift-upload-kids-switch" checked={form.kids} onCheckedChange={(on) => setForm({ ...form, kids: on, ...(!on && { age: "" }) })} disabled={!!busy} />
          </label>
          {form.kids && <Choices name="age" text="Age group (tap again to clear)" options={KIDS_AGES} display={ageLabel} required={false} cols="grid-cols-4" form={form} setForm={setForm} />}
          <Field name="upi" text="Your UPI ID (for buyer payments)" form={form} setForm={setForm} placeholder="name@bank" autoComplete="off" />
          <p className="-mt-2 text-xs text-[#71717A]">Leave empty and buyers can only pay cash on delivery.</p>
          <button data-testid="thrift-upload-submit" type="submit" disabled={!!busy} className="w-full h-12 rounded-full bg-[#C5A059] text-[#0F0F0F] font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#B38F46] transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> <span data-testid="thrift-upload-progress">{busy}</span></> : "Post to Thrift Closet"}
          </button>
          <p className="text-center text-xs text-[#71717A]">Manage pieces later from <Link to="/admin/listings" className="underline">All Listings</Link>.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
